import styled from 'styled-components';

import { rem, mediaUp } from 'approot/shared/style';
import {
  LinkButton,
  SecondaryButton,
} from 'approot/shared/styles/buttons/buttons';
import {
  PStyled,
  font16,
  SmallStyled,
  purpleLinkBase,
  font14,
} from 'approot/shared/styles/typography.style';
import {
  borderRadius16,
  borderRadius8,
} from 'approot/shared/styles/borders.style';
import {
  spacing8,
  spacing16,
  spacing32,
  spacing12,
  spacing4,
} from 'approot/shared/styles/spacing.style';
import {
  grey5,
  grey7,
  grey8,
  grey10,
  red8,
} from 'approot/shared/styles/colours.style';
import { THUMBNAIL_WIDTH } from 'lib/form/image-picker/image-picker.constants';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { Ellipsis } from 'approot/shared/styles/ellipsis.style';

export const FileDropperPlaceholder = styled.div`
  color: ${grey10};

  svg {
    margin-bottom: ${rem(spacing16)};
  }
`;

export const FileDropperPlaceholderButton = styled(LinkButton)`
  ${purpleLinkBase}
  ${font16}
`;

type FileDropperFieldProps = {
  $activeDrag?: boolean;
  $focus?: boolean;
  $thumbnailStyling?: boolean;
  $square?: boolean;
  $isLoading?: boolean;
  $hasError?: boolean;
};

export const FileDropperField = styled.div<FileDropperFieldProps>`
  border: 1px dashed ${({ $hasError }) => ($hasError ? red8 : grey5)};
  border-radius: ${rem(borderRadius16)};
  color: ${grey8};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: ${rem(20)};
  margin: 0 auto;
  max-width: ${rem(600)};
  min-height: ${rem(120)};
  padding: ${rem(spacing16)};
  outline: none;
  width: 100%;
  text-align: center;

  ${({ $activeDrag }) =>
    $activeDrag &&
    `
    opacity: 0.5;
  `};

  ${({ $focus }) =>
    $focus &&
    `
    border-color: ${red8};
    border-width: 2px;
  `}

  /* Overriding styles for content thumbnails e.g. add stimulus or add assessment PDF file droppers  */
  ${({ $thumbnailStyling }) =>
    $thumbnailStyling &&
    `
    min-height: unset;
    height: unset;
    color: ${grey8};
    position: relative;
    flex-grow: 1;
    padding: ${rem(spacing8)};
  `}
  /* Display as a thumbnail sized square  */
  ${({ $square }) =>
    $square &&
    `
    min-height: unset;
    min-width: unset;
    width: ${rem(THUMBNAIL_WIDTH)};
    height: ${rem(THUMBNAIL_WIDTH)};
  `}

  ${({ $isLoading }) =>
    $isLoading &&
    `
    cursor: not-allowed;
  `}

  &:hover ${FileDropperPlaceholderButton} {
    text-decoration: underline;
  }
`;

export const FileDropperInput = styled.input`
  border: none;
  width: 0;
  height: 0;
  outline: none;
  opacity: 0;
  position: absolute;

  &:focus {
    outline: none;
    border: none;
  }
`;

export const FileDropperLabel = styled.label`
  display: block;
  cursor: pointer;
`;

export const FileDropperButton = styled(SecondaryButton)`
  display: block;
`;

export const FileDropperButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${rem(spacing16)};

  button {
    margin: 0;
    margin-bottom: ${rem(spacing8)};
  }

  ${mediaUp.md`
    flex-direction: row;

    button {
      margin: 0 ${rem(spacing4)};
      margin-bottom: 0;
    
      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  `}
`;

export const FileDropperEditLoadingContainerStyled = styled.div`
  padding: ${rem(spacing32)} 0;
  text-align: center;

  ${PStyled} {
    color: ${grey8};
    margin-bottom: ${rem(spacing32)};
  }
`;

export const FileDropperText = styled(PStyled).attrs({})`
  margin-bottom: 0;
`;

export const FileDropperSecondaryText = styled(PStyled).attrs({
  colour: grey7,
})`
  ${font14}
  display: block;
  margin-bottom: 0;
`;

type FileDropperProps = {
  $backgroundColor?: string;
};
export const FileDropper = styled.div<FileDropperProps>`
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius16)};
  background-color: ${({ $backgroundColor = 'transparent' }) =>
    $backgroundColor};
  margin: 0 auto;
  max-width: ${rem(600)};
  padding: ${rem(spacing12)};
  width: 100%;
`;

export const FileDropperProgress = styled.div`
  display: flex;
`;

export const FileDropperProgressIndicator = styled.div`
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
  width: ${rem(48)};
  height: ${rem(48)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const FileDropperProgressDetail = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-basis: 100%;
  padding: 0 ${rem(spacing12)};
  text-align: left;
`;

export const FileDropperProgressActions = styled.div`
  flex-shrink: 0;
`;

export const FileDropperProgressName = styled(Ellipsis)`
  ${font16}
  color: ${grey10};
  margin-bottom: 0;
`;

export const FileDropperProgressSize = styled(SmallStyled)`
  color: ${grey7};
  display: block;
  margin-bottom: 0;
`;

export const FileDropperProgressBar = styled.div`
  display: flex;
  align-items: center;
`;

export const FileDropperProgressBarAmount = styled(SmallStyled)`
  color: ${grey10};
  display: inline-block;
  margin-bottom: 0;
  margin-left: ${rem(spacing8)};
  min-width: ${rem(40)};
  text-align: right;
`;

export const FileDropperProgressError = styled(ErrorMessageStyled)`
  margin-top: 0;
`;
